import React, { useState, useEffect, useMemo } from 'react';
import { Box, Text, Button } from '@primer/react';
// Import the DSA data
import dsaPatterns from '../data/dsaData.js';

const DSAFlashcard = () => {
    // State management
    const [searchTerm, setSearchTerm] = useState('');
    const [currentAlgorithm, setCurrentAlgorithm] = useState(null);
    const [allAlgorithms, setAllAlgorithms] = useState([]);
    const [activeDifficulties, setActiveDifficulties] = useState(new Set([1, 2, 3])); // Default to all difficulties
    const [activeCategories, setActiveCategories] = useState(new Set());
    const [allCategories, setAllCategories] = useState([]);
    const [showCategoryModal, setShowCategoryModal] = useState(false);
    const [hintLevel, setHintLevel] = useState(0); // 0: just code, 1: useWhen, 2: whyUseIt, 3: howItWorks, 4: full reveal
    const [isAnswered, setIsAnswered] = useState(false);
    const [filteredSuggestions, setFilteredSuggestions] = useState([]);
    const [showSuggestions, setShowSuggestions] = useState(false);
    const [showWrongMessage, setShowWrongMessage] = useState(false);

    // Build algorithm index
    useEffect(() => {
        const algorithms = [];
        const categories = [];

        for (const [categoryName, categoryData] of Object.entries(dsaPatterns.categories)) {
            categories.push(categoryName);
            for (const [subcategoryName, subcategoryData] of Object.entries(categoryData.subcategories)) {
                for (const [algorithmName, algorithmData] of Object.entries(subcategoryData.algorithms)) {
                    algorithms.push({
                        name: algorithmName,
                        category: categoryName,
                        subcategory: subcategoryName,
                        data: algorithmData
                    });
                }
            }
        }

        setAllAlgorithms(algorithms);
        setAllCategories(categories);
        setActiveCategories(new Set(categories)); // Initialize with all categories selected
    }, []);

    // Filter algorithms based on selected difficulties and categories
    const filteredAlgorithms = useMemo(() => {
        return allAlgorithms.filter(algo => 
            activeDifficulties.has(algo.data.difficulty) && 
            activeCategories.has(algo.category)
        );
    }, [allAlgorithms, activeDifficulties, activeCategories]);

    // Set initial random algorithm when filtered algorithms change
    useEffect(() => {
        if (filteredAlgorithms.length > 0) {
            selectRandomAlgorithm(filteredAlgorithms);
        }
    }, [filteredAlgorithms]);

    // Select random algorithm
    const selectRandomAlgorithm = (algorithms = filteredAlgorithms) => {
        if (algorithms.length > 0) {
            const randomIndex = Math.floor(Math.random() * algorithms.length);
            setCurrentAlgorithm(algorithms[randomIndex]);
            setHintLevel(0);
            setIsAnswered(false);
            setSearchTerm('');
            setShowSuggestions(false);
            setShowWrongMessage(false);
        }
    };

    // Filter suggestions based on search term (only from current difficulty filter)
    useEffect(() => {
        if (searchTerm.length > 0) {
            const filtered = filteredAlgorithms
                .filter(algo => 
                    algo.name.toLowerCase().includes(searchTerm.toLowerCase())
                )
                .slice(0, 5) // Limit to 5 suggestions
                .map(algo => algo.name);
            
            setFilteredSuggestions(filtered);
            setShowSuggestions(true);
        } else {
            setFilteredSuggestions([]);
            setShowSuggestions(false);
        }
    }, [searchTerm, filteredAlgorithms]);

    // Check if user input matches the correct algorithm
    useEffect(() => {
        if (currentAlgorithm && searchTerm.toLowerCase().trim() === currentAlgorithm.name.toLowerCase()) {
            setIsAnswered(true);
            setHintLevel(4); // Full reveal
            setShowSuggestions(false);
            setShowWrongMessage(false);
        }
    }, [searchTerm, currentAlgorithm]);

    // Handle hint button click
    const handleHintClick = () => {
        if (hintLevel < 4) {
            setHintLevel(hintLevel + 1);
            setShowWrongMessage(false);
        }
    };

    // Handle next button click
    const handleNextClick = () => {
        selectRandomAlgorithm();
    };

    // Handle suggestion selection
    const handleSuggestionClick = (suggestion) => {
        setSearchTerm(suggestion);
        // Let the useEffect handle the checking automatically
        // setShowSuggestions(false); - This will be handled by the useEffect
    };

    // Handle difficulty change
    const handleDifficultyChange = (difficulty) => {
        setActiveDifficulties(prev => {
            const newSet = new Set(prev);
            if (newSet.has(difficulty)) {
                newSet.delete(difficulty);
            } else {
                newSet.add(difficulty);
            }
            // Ensure at least one difficulty is always selected
            if (newSet.size === 0) {
                return new Set([1, 2, 3]);
            }
            return newSet;
        });
    };

    // Handle category change
    const handleCategoryChange = (category) => {
        setActiveCategories(prev => {
            const newSet = new Set(prev);
            if (newSet.has(category)) {
                newSet.delete(category);
            } else {
                newSet.add(category);
            }
            if (newSet.size === 0) {
                return new Set(allCategories);
            }
            return newSet;
        });
    };

    // Toggle all categories
    const toggleAllCategories = () => {
        if (activeCategories.size === allCategories.length) {
            setActiveCategories(new Set()); // Deselect all
        } else {
            setActiveCategories(new Set(allCategories)); // Select all
        }
    };

    // Handle Enter key press on input
    const handleKeyPress = (e) => {
        if (e.key === 'Enter' && searchTerm.trim() && !isAnswered) {
            // Check if it's a wrong answer
            if (currentAlgorithm && searchTerm.toLowerCase().trim() !== currentAlgorithm.name.toLowerCase()) {
                // Show wrong message and automatically give a hint
                setShowWrongMessage(true);
                setShowSuggestions(false);
                
                // Auto-advance hint level if not at max
                if (hintLevel < 4) {
                    setTimeout(() => {
                        setHintLevel(prev => prev + 1);
                    }, 1000); // Wait 1 second before showing hint
                }
                
                // Hide wrong message after 3 seconds
                setTimeout(() => {
                    setShowWrongMessage(false);
                }, 3000);
            }
        }
    };

    // Keyboard navigation
    useEffect(() => {
        const handleKeyDown = (e) => {
            if (e.key === 'Escape') {
                if (showCategoryModal) {
                    setShowCategoryModal(false);
                    return;
                }
            }
        };

        document.addEventListener('keydown', handleKeyDown);
        return () => document.removeEventListener('keydown', handleKeyDown);
    }, [showCategoryModal]);

    // Get hint button text
    const getHintButtonText = () => {
        switch (hintLevel) {
            case 0: return "Give me a hint";
            case 1: return "Give me another hint";
            case 2: return "One more hint";
            case 3: return "Reveal answer";
            default: return "Answer revealed";
        }
    };

    // Get difficulty label
    const getDifficultyLabel = (difficulty) => {
        switch (difficulty) {
            case 1: return 'Core';
            case 2: return 'Advanced';
            case 3: return 'Expert';
            default: return 'Unknown';
        }
    };

    // Get algorithm keywords (same function as DSAGuide)
    const getAlgorithmKeywords = (algorithmName) => {
        if (!dsaPatterns.patternKeywords || !dsaPatterns.patternKeywords.keywords) {
            return null;
        }
        
        const patternData = dsaPatterns.patternKeywords.keywords[algorithmName];
        if (!patternData || !patternData.keywords) return null;
        
        return (
            <div className="algorithm-keywords">
                <strong>🎯 Pattern Keywords:</strong> {patternData.keywords}
            </div>
        );
    };

    if (!currentAlgorithm) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
                <Text>Loading flashcard...</Text>
            </Box>
        );
    }

    return (
        <Box mb={6}>
            {/* Import all the styles from DSAGuide */}
            <style>{`
                /* DSA Guide Specific Styles - Copied from DSAGuide.js */
                .search-highlight {
                    background: linear-gradient(135deg, #fef3c7, #fde68a);
                    padding: 0.2rem 0.4rem;
                    border-radius: 4px;
                    font-weight: 600;
                    color: #92400e;
                }
                
                .flashcard-container {
                    max-width: 800px;
                    margin: 0 auto;
                    padding: 1rem;
                }
                
                .correct-input {
                    background: #f0fdf4 !important;
                    border-color: #22c55e !important;
                }
                
                .shake-animation {
                    animation: shake 0.5s ease-in-out;
                }
                
                @keyframes shake {
                    0%, 100% { transform: translateX(0); }
                    25% { transform: translateX(-5px); }
                    75% { transform: translateX(5px); }
                }
                
                .pattern-card {
                    background: white;
                    border-radius: 16px;
                    padding: 2rem;
                    box-shadow: 0 4px 20px rgba(0,0,0,0.08);
                    border: 1px solid rgba(0,0,0,0.05);
                    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
                    position: relative;
                    overflow: hidden;
                }
                
                .pattern-card::before {
                    content: '';
                    position: absolute;
                    top: 0;
                    left: 0;
                    right: 0;
                    height: 4px;
                    background: linear-gradient(90deg, #3b82f6, #8b5cf6);
                }
                
                .pattern-card.difficulty-1::before {
                    background: #22c55e;
                }
                
                .pattern-card.difficulty-2::before {
                    background: #f59e0b;
                }
                
                .pattern-card.difficulty-3::before {
                    background: #ef4444;
                }
                
                .pattern-header {
                    display: flex;
                    justify-content: space-between;
                    align-items: flex-start;
                    margin-bottom: 1.5rem;
                }
                
                .pattern-title {
                    font-size: 1.5rem;
                    font-weight: 700;
                    color: #1a202c;
                    margin-bottom: 0.75rem;
                    line-height: 1.2;
                    word-wrap: break-word;
                    overflow-wrap: break-word;
                    max-width: 100%;
                }
                
                .pattern-title.hidden {
                    color: #9ca3af;
                    font-style: italic;
                }
                
                .pattern-meta {
                    display: flex;
                    gap: 0.75rem;
                    flex-wrap: wrap;
                    margin-bottom: 1rem;
                }
                
                .pattern-complexity,
                .pattern-space {
                    background: #f7fafc;
                    color: #4a5568;
                    padding: 0.25rem 0.75rem;
                    border-radius: 20px;
                    font-size: 0.875rem;
                    font-weight: 600;
                    border: 1px solid #e2e8f0;
                }
                
                .pattern-complexity {
                    background: linear-gradient(135deg, #dbeafe, #bfdbfe);
                    color: #1e40af;
                    border-color: #bfdbfe;
                }
                
                .pattern-space {
                    background: linear-gradient(135deg, #dcfce7, #bbf7d0);
                    color: #166534;
                    border-color: #bbf7d0;
                }
                
                .pattern-description {
                    color: #374151;
                    margin-bottom: 1rem;
                    line-height: 1.6;
                    font-size: 0.9rem;
                    word-wrap: break-word;
                    overflow-wrap: break-word;
                    max-width: 100%;
                }
                
                .pattern-section {
                    margin-bottom: 0.8rem;
                    font-size: 0.9rem;
                    line-height: 1.5;
                    word-wrap: break-word;
                    overflow-wrap: break-word;
                    max-width: 100%;
                }
                
                .pattern-section strong {
                    color: #111827;
                    font-weight: 600;
                }
                
                .pattern-use-case {
                    background: linear-gradient(135deg, #fef3c7, #fde68a);
                    padding: 1rem;
                    border-radius: 8px;
                    margin: 1rem 0;
                    font-size: 0.9rem;
                    color: #92400e;
                    line-height: 1.5;
                }
                
                .key-points {
                    background: linear-gradient(135deg, #dbeafe, #bfdbfe);
                    padding: 1rem;
                    border-radius: 8px;
                    margin: 1rem 0;
                    font-size: 0.9rem;
                    color: #1e40af;
                    line-height: 1.5;
                }
                
                .algorithm-keywords {
                    background: linear-gradient(135deg, #f3e8ff, #e9d5ff);
                    padding: 1rem;
                    border-radius: 8px;
                    margin: 1rem 0;
                    font-size: 0.9rem;
                    color: #7c3aed;
                    line-height: 1.5;
                    border: 1px solid #e9d5ff;
                }
                
                .algorithm-keywords strong {
                    color: #6b21a8;
                    font-weight: 600;
                }
                
                .code-section {
                    margin-top: 1.5rem;
                    border-top: 1px solid #e2e8f0;
                    padding-top: 1.5rem;
                }
                
                .code-snippet {
                    background: #1a202c;
                    color: #e2e8f0;
                    padding: 1.5rem;
                    border-radius: 12px;
                    font-family: 'JetBrains Mono', 'Fira Code', 'SF Mono', monospace;
                    font-size: 0.875rem;
                    line-height: 1.6;
                    overflow-x: auto;
                    border: 1px solid #2d3748;
                    margin-bottom: 1rem;
                    box-shadow: inset 0 2px 8px rgba(0,0,0,0.3);
                    white-space: pre;
                }
                
                .difficulty-badge {
                    display: inline-flex;
                    align-items: center;
                    gap: 0.25rem;
                    padding: 0.25rem 0.5rem;
                    border-radius: 9999px;
                    font-size: 0.75rem;
                    font-weight: 600;
                    text-transform: uppercase;
                    letter-spacing: 0.025em;
                }
                
                .difficulty-badge-1 {
                    background-color: #dcfce7;
                    color: #166534;
                }
                
                .difficulty-badge-2 {
                    background-color: #fef3c7;
                    color: #92400e;
                }
                
                .difficulty-badge-3 {
                    background-color: #fecaca;
                    color: #991b1b;
                }
                
                @media (max-width: 768px) {
                    .flashcard-container {
                        padding: 0.5rem;
                        width: 100%;
                        max-width: 100%;
                        box-sizing: border-box;
                        overflow-x: hidden;
                    }
                    
                    .pattern-card {
                        padding: 1rem;
                    }
                    
                    .pattern-title {
                        font-size: 1.1rem;
                    }
                    
                    .code-snippet {
                        font-size: 0.8rem;
                        padding: 1rem;
                        overflow-x: auto;
                        white-space: pre;
                    }
                    
                    /* Stack controls vertically on mobile */
                    .mobile-stack {
                        flex-direction: column !important;
                        align-items: stretch !important;
                    }
                    
                    .mobile-stack > div {
                        width: 100% !important;
                        justify-content: center !important;
                    }
                    
                    /* Ensure inputs don't zoom on iOS */
                    input {
                        font-size: 16px !important;
                    }
                }
                
                /* Extra small screens (phones) */
                @media (max-width: 480px) {
                    .flashcard-container {
                        padding: 0.25rem;
                        margin: 0;
                    }
                    
                    .pattern-card {
                        padding: 0.75rem;
                        margin: 0.5rem 0;
                    }
                    
                    .pattern-header {
                        margin-bottom: 1rem;
                    }
                    
                    .pattern-meta {
                        gap: 0.5rem;
                    }
                }
                
                /* Modal styles */
                .modal-overlay {
                    position: fixed;
                    top: 0;
                    left: 0;
                    right: 0;
                    bottom: 0;
                    background: rgba(0, 0, 0, 0.5);
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    z-index: 1000;
                    backdrop-filter: blur(4px);
                    animation: fadeIn 0.2s ease-out;
                }
                
                .modal-content {
                    background: white;
                    border-radius: 12px;
                    box-shadow: 0 20px 40px rgba(0, 0, 0, 0.15);
                    max-width: 90vw;
                    max-height: 80vh;
                    overflow: hidden;
                    animation: slideUp 0.3s ease-out;
                    border: 1px solid #e2e8f0;
                }
                
                .modal-header {
                    padding: 1.5rem;
                    border-bottom: 1px solid #e2e8f0;
                    background: #f8fafc;
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                }
                
                .modal-body {
                    padding: 1.5rem;
                    overflow-y: auto;
                    max-height: 60vh;
                }
                
                .modal-footer {
                    padding: 1rem 1.5rem;
                    border-top: 1px solid #e2e8f0;
                    background: #f8fafc;
                    display: flex;
                    gap: 1rem;
                    justify-content: flex-end;
                }
                
                @keyframes fadeIn {
                    from { opacity: 0; }
                    to { opacity: 1; }
                }
                
                @keyframes slideUp {
                    from { 
                        opacity: 0;
                        transform: translateY(20px) scale(0.95);
                    }
                    to { 
                        opacity: 1;
                        transform: translateY(0) scale(1);
                    }
                }
                
                @media (max-width: 768px) {
                    .modal-content {
                        margin: 1rem;
                        max-width: calc(100vw - 2rem);
                        max-height: calc(100vh - 2rem);
                    }
                    
                    .modal-header {
                        padding: 1rem;
                    }
                    
                    .modal-body {
                        padding: 1rem;
                        max-height: calc(70vh - 4rem);
                    }
                    
                    .modal-footer {
                        padding: 1rem;
                        flex-direction: column;
                    }
                }
            `}</style>
            <div className="flashcard-container">
                {/* Clean, Simple Guess Section */}
                <Box
                    bg="canvas.subtle"
                    p={4}
                    borderRadius={2}
                    border="1px solid"
                    borderColor="border.default"
                    mb={4}
                    sx={{
                        width: '100%',
                        maxWidth: '100%',
                        boxSizing: 'border-box'
                    }}
                >
                    {/* Filters Section */}
                    <Box mb={3}>
                        {/* Difficulty Filters */}
                        <Box mb={3}>
                            <Box display="flex" alignItems="center" gap={2} flexWrap="wrap" mb={2}>
                                <Text fontSize={1} fontWeight="semibold" color="fg.muted">
                                    Difficulty:
                                </Text>
                                <Box display="flex" gap={2} flexWrap="wrap">
                                {[1, 2, 3].map(difficulty => {
                                    const label = getDifficultyLabel(difficulty);
                                    const isActive = activeDifficulties.has(difficulty);
                                    const colors = {
                                        1: { bg: '#22c55e', text: '#166534' },
                                        2: { bg: '#f59e0b', text: '#92400e' },
                                        3: { bg: '#ef4444', text: '#991b1b' }
                                    };
                                    
                                    return (
                                        <Box
                                            key={difficulty}
                                            display="flex"
                                            alignItems="center"
                                            gap={1}
                                            sx={{
                                                cursor: 'pointer',
                                                '&:hover': {
                                                    opacity: 0.8
                                                }
                                            }}
                                            onClick={() => handleDifficultyChange(difficulty)}
                                        >
                                            <Box
                                                as="input"
                                                type="checkbox"
                                                checked={isActive}
                                                onChange={() => handleDifficultyChange(difficulty)}
                                                sx={{
                                                    accentColor: colors[difficulty].bg,
                                                    cursor: 'pointer'
                                                }}
                                            />
                                            <Text
                                                fontSize={[0, 1]}
                                                fontWeight="semibold"
                                                color={isActive ? colors[difficulty].text : 'fg.muted'}
                                                sx={{ 
                                                    cursor: 'pointer',
                                                    whiteSpace: 'nowrap'
                                                }}
                                            >
                                                {label}
                                            </Text>
                                        </Box>
                                    );
                                })}
                                </Box>
                            </Box>
                        </Box>
                        
                        {/* Category Filter Button */}
                        <Box mb={2}>
                            <Box
                                as="button"
                                onClick={() => setShowCategoryModal(true)}
                                bg="success.subtle"
                                color="success.fg"
                                border="1px solid"
                                borderColor="success.muted"
                                px={3}
                                py={1}
                                borderRadius={2}
                                fontSize={1}
                                fontWeight={500}
                                sx={{
                                    cursor: 'pointer',
                                    transition: 'all 0.2s ease',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    gap: 2,
                                    '&:hover': {
                                        bg: 'success.muted',
                                        borderColor: 'success.emphasis',
                                        transform: 'translateY(-1px)'
                                    },
                                    '&:active': {
                                        transform: 'scale(0.98)'
                                    }
                                }}
                            >
                                <Box as="span" fontSize={2}>🏷️</Box>
                                <Box as="span">Categories ({activeCategories.size})</Box>
                            </Box>
                        </Box>
                    </Box>

                    {/* Input Field */}
                    <Box mb={3}>
                        <Box
                            display="flex"
                            alignItems="center"
                            bg="canvas.default"
                            border="2px solid"
                            borderColor={isAnswered ? "success.emphasis" : "border.default"}
                            borderRadius={2}
                            px={3}
                            py={2}
                            sx={{
                                width: '100%',
                                maxWidth: '100%',
                                boxSizing: 'border-box',
                                transition: 'border-color 0.2s ease',
                                '&:focus-within': {
                                    borderColor: 'accent.emphasis'
                                }
                            }}
                        >
                            <Text mr={2}>🔍</Text>
                            <Box
                                as="input"
                                type="text"
                                id="algorithmSearch"
                                placeholder={`Guess the algorithm (${filteredAlgorithms.length} available)`}
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                onKeyDown={handleKeyPress}
                                disabled={isAnswered}
                                autoComplete="off"
                                autoCapitalize="off"
                                autoCorrect="off"
                                spellCheck="false"
                                sx={{
                                    flex: 1,
                                    border: 'none',
                                    outline: 'none',
                                    fontSize: [2, 3],
                                    bg: 'transparent',
    
                                    minWidth: 0,
                                    width: '100%',
                                    color: isAnswered ? 'success.fg' : 'fg.default',
                                    fontWeight: isAnswered ? 'semibold' : 'normal'
                                }}
                            />

                        </Box>

                        {/* Suggestions Dropdown */}
                        {showSuggestions && filteredSuggestions.length > 0 && (
                            <Box
                                position="relative"
                                mt={1}
                            >
                                <Box
                                    position="absolute"
                                    top={0}
                                    left={0}
                                    right={0}
                                    bg="canvas.default"
                                    border="1px solid"
                                    borderColor="border.default"
                                    borderRadius={1}
                                    boxShadow="0 4px 20px rgba(0,0,0,0.1)"
                                    zIndex={1000}
                                    maxHeight="180px"
                                    overflowY="auto"
                                >
                                    {filteredSuggestions.map((suggestion, index) => (
                                        <Box
                                            key={index}
                                            p={2}
                                            sx={{
                                                cursor: 'pointer',
                                                borderBottom: '1px solid',
                                                borderColor: 'border.muted',
                                                minHeight: '44px',
                                                display: 'flex',
                                                alignItems: 'center',
                                                '&:hover': {
                                                    bg: 'canvas.subtle'
                                                },
                                                '&:last-child': {
                                                    borderBottom: 'none'
                                                }
                                            }}
                                            onClick={() => handleSuggestionClick(suggestion)}
                                        >
                                            {suggestion}
                                        </Box>
                                    ))}
                                </Box>
                            </Box>
                        )}
                    </Box>

                    {/* Action Buttons - More Spaced */}
                    <Box
                        display="flex"
                        justifyContent="center"
                        alignItems="center"
                        flexWrap="wrap"
                        sx={{ gap: '1rem' }}
                    >
                        {hintLevel < 4 && !isAnswered && (
                            <Box
                                as="button"
                                onClick={handleHintClick}
                                px={3}
                                py={2}
                                mr={3}
                                sx={{
                                    bg: 'attention.subtle',
                                    color: 'attention.fg',
                                    border: '1px solid',
                                    borderColor: 'attention.muted',
                                    borderRadius: 2,
                                    fontSize: 1,
                                    fontWeight: 500,
                                    cursor: 'pointer',
                                    transition: 'all 0.2s ease',
                                    textDecoration: 'none',
                                    whiteSpace: 'nowrap',
                                    '&:hover': {
                                        bg: 'attention.muted',
                                        borderColor: 'attention.emphasis',
                                        textDecoration: 'none'
                                    }
                                }}
                            >
                                <Box display="flex" alignItems="center" gap={2}>
                                    💡 {getHintButtonText()}
                                </Box>
                            </Box>
                        )}
                        
                        <Box
                            as="button"
                            onClick={handleNextClick}
                            px={3}
                            py={2}
                            sx={{
                                bg: 'accent.subtle',
                                color: 'accent.fg',
                                border: '1px solid',
                                borderColor: 'accent.muted',
                                borderRadius: 2,
                                fontSize: 1,
                                fontWeight: 500,
                                cursor: 'pointer',
                                transition: 'all 0.2s ease',
                                textDecoration: 'none',
                                whiteSpace: 'nowrap',
                                '&:hover': {
                                    bg: 'accent.muted',
                                    borderColor: 'accent.emphasis',
                                    textDecoration: 'none'
                                }
                            }}
                        >
                            <Box display="flex" alignItems="center" gap={2}>
                                🔄 Next
                            </Box>
                        </Box>
                    </Box>
                </Box>

                {/* Success Message */}
                {isAnswered && (
                    <Box
                        bg="success.subtle"
                        p={3}
                        borderRadius={1}
                        border="1px solid"
                        borderColor="success.muted"
                        mb={3}
                        textAlign="center"
                    >
                        <Text color="success.fg" fontSize={2} fontWeight="bold">
                            🎉 Correct! You identified: {currentAlgorithm.name}
                        </Text>
                    </Box>
                )}

                {/* Wrong Answer Message */}
                {showWrongMessage && (
                    <Box
                        bg="danger.subtle"
                        p={3}
                        borderRadius={1}
                        border="1px solid"
                        borderColor="danger.muted"
                        mb={3}
                        textAlign="center"
                        className="shake-animation"
                    >
                        <Text color="danger.fg" fontSize={2} fontWeight="bold">
                            ❌ Not quite right! Let me give you a hint...
                        </Text>
                    </Box>
                )}

                {/* Pattern Card - Exact copy of DSAGuide structure */}
                <div className={`pattern-card difficulty-${currentAlgorithm.data.difficulty}`}>
                    <div className="pattern-header">
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '1rem', width: '100%' }}>
                            <div>
                                {(isAnswered || hintLevel >= 4) && (
                                    <h4 className="pattern-title">
                                        {currentAlgorithm.name}
                                    </h4>
                                )}
                                <div className="pattern-meta">
                                    <span className="pattern-complexity">
                                        Time: {currentAlgorithm.data.complexity}
                                    </span>
                                    <span className="pattern-space">
                                        Space: {currentAlgorithm.data.spaceComplexity || 'O(1)'}
                                    </span>
                                </div>
                            </div>
                            <span className={`difficulty-badge difficulty-badge-${currentAlgorithm.data.difficulty}`}>
                                ● {getDifficultyLabel(currentAlgorithm.data.difficulty)}
                            </span>
                        </div>
                    </div>
                    
                    {/* Show how it works only when revealed */}
                    {(isAnswered || hintLevel >= 4) && (
                        <div className="pattern-section">
                            <strong>How it works:</strong> {currentAlgorithm.data.howItWorks}
                        </div>
                    )}
                    
                    {/* Show why use it only when revealed */}
                    {(isAnswered || hintLevel >= 4) && (
                        <div className="pattern-section">
                            <strong>Why use it:</strong> {currentAlgorithm.data.whyUseIt}
                        </div>
                    )}
                    
                    {/* Show description as third hint (appears at top when revealed) */}
                    {(isAnswered || hintLevel >= 3) && (
                        <div className="pattern-section">
                            <strong>What it does:</strong> {currentAlgorithm.data.description}
                        </div>
                    )}
                    
                    {/* Show use when as first hint */}
                    {(isAnswered || hintLevel >= 1) && (
                        <div className="pattern-use-case">
                            <strong>Use when:</strong> {currentAlgorithm.data.useWhen}
                        </div>
                    )}
                    
                    {/* Show key points as second hint */}
                    {(isAnswered || hintLevel >= 2) && (
                        <div className="key-points">
                            <strong>🔑 Key Points:</strong> {currentAlgorithm.data.keyPoints}
                        </div>
                    )}
                    
                    {/* Show keywords only when revealed */}
                    {(isAnswered || hintLevel >= 4) && getAlgorithmKeywords(currentAlgorithm.name)}
                    
                    {/* Code section - always visible */}
                    <div className="code-section">
                        <div className="code-snippet">
                            {currentAlgorithm.data.codeExample}
                        </div>
                    </div>
                </div>
            </div>

            {/* Category Selection Modal */}
            {showCategoryModal && (
                <div className="modal-overlay" onClick={() => setShowCategoryModal(false)}>
                    <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-header">
                            <Box display="flex" alignItems="center" gap={2}>
                                <Text fontSize={3} fontWeight="bold" color="fg.default">
                                    🏷️ Select Categories
                                </Text>
                                <Box 
                                    bg="success.emphasis" 
                                    color="canvas.default" 
                                    px={2} 
                                    py={1} 
                                    borderRadius={1} 
                                    fontSize={1}
                                    fontWeight="bold"
                                >
                                    {activeCategories.size}/{allCategories.length}
                                </Box>
                            </Box>
                            <Box
                                as="button"
                                onClick={() => setShowCategoryModal(false)}
                                bg="transparent"
                                border="none"
                                color="fg.muted"
                                fontSize={4}
                                sx={{
                                    cursor: 'pointer',
                                    padding: '0.5rem',
                                    borderRadius: '50%',
                                    lineHeight: 1,
                                    '&:hover': {
                                        bg: 'danger.subtle',
                                        color: 'danger.fg'
                                    }
                                }}
                                title="Close"
                            >
                                ✕
                            </Box>
                        </div>
                        
                        <div className="modal-body">
                            {/* Quick Actions */}
                            <Box display="flex" gap={2} mb={4} flexWrap="wrap">
                                <Box
                                    as="button"
                                    onClick={toggleAllCategories}
                                    bg="accent.subtle"
                                    color="accent.fg"
                                    border="1px solid"
                                    borderColor="accent.muted"
                                    px={3}
                                    py={2}
                                    borderRadius={1}
                                    fontSize={1}
                                    fontWeight={500}
                                    sx={{
                                        cursor: 'pointer',
                                        transition: 'all 0.2s ease',
                                        flex: ['1', '0 0 auto'],
                                        minHeight: '40px',
                                        '&:hover': {
                                            bg: 'accent.muted',
                                            borderColor: 'accent.emphasis'
                                        }
                                    }}
                                >
                                    {activeCategories.size === allCategories.length ? 'Deselect All' : 'Select All'}
                                </Box>
                            </Box>
                            
                            {/* Categories Grid */}
                            <Box 
                                display="grid" 
                                gridTemplateColumns={["1fr", "repeat(2, 1fr)"]}
                                gap={3}
                                sx={{
                                    '@media (max-width: 480px)': {
                                        gridTemplateColumns: '1fr'
                                    }
                                }}
                            >
                                {allCategories.map(category => {
                                    const isActive = activeCategories.has(category);
                                    const categoryData = dsaPatterns.categories[category];
                                    const algorithmCount = Object.values(categoryData.subcategories || {}).reduce((total, subcategory) => {
                                        return total + Object.keys(subcategory.algorithms || {}).length;
                                    }, 0);
                                    
                                    return (
                                        <Box
                                            key={category}
                                            bg={isActive ? "success.subtle" : "canvas.subtle"}
                                            border="2px solid"
                                            borderColor={isActive ? "success.emphasis" : "border.default"}
                                            borderRadius={2}
                                            p={3}
                                            sx={{
                                                cursor: 'pointer',
                                                transition: 'all 0.2s ease',
                                                '&:hover': {
                                                    bg: isActive ? 'success.muted' : 'accent.subtle',
                                                    borderColor: isActive ? 'success.emphasis' : 'accent.muted',
                                                    transform: 'translateY(-2px)',
                                                    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)'
                                                },
                                                '&:active': {
                                                    transform: 'scale(0.98)'
                                                }
                                            }}
                                            onClick={() => handleCategoryChange(category)}
                                        >
                                            <Box display="flex" alignItems="flex-start" gap={2} mb={2}>
                                                <Box
                                                    as="input"
                                                    type="checkbox"
                                                    checked={isActive}
                                                    onChange={() => handleCategoryChange(category)}
                                                    sx={{
                                                        accentColor: '#22c55e',
                                                        cursor: 'pointer',
                                                        width: '18px',
                                                        height: '18px',
                                                        flexShrink: 0,
                                                        marginTop: '2px'
                                                    }}
                                                />
                                                <Box flex="1">
                                                    <Box display="flex" alignItems="center" gap={2} mb={1}>
                                                        <Text fontSize={2}>{categoryData?.sectionIcon}</Text>
                                                        <Text
                                                            fontSize={2}
                                                            fontWeight="semibold"
                                                            color="fg.default"
                                                            sx={{ 
                                                                cursor: 'pointer',
                                                                userSelect: 'none',
                                                                lineHeight: 1.2
                                                            }}
                                                        >
                                                            {category}
                                                        </Text>
                                                    </Box>
                                                    <Text
                                                        fontSize={1}
                                                        color="fg.muted"
                                                        sx={{
                                                            cursor: 'pointer',
                                                            userSelect: 'none'
                                                        }}
                                                    >
                                                        {algorithmCount} algorithm{algorithmCount !== 1 ? 's' : ''}
                                                    </Text>
                                                </Box>
                                            </Box>
                                        </Box>
                                    );
                                })}
                            </Box>
                        </div>
                        
                        <div className="modal-footer">
                            <Box
                                as="button"
                                onClick={() => setShowCategoryModal(false)}
                                bg="accent.subtle"
                                color="accent.fg"
                                border="1px solid"
                                borderColor="accent.muted"
                                px={4}
                                py={2}
                                borderRadius={1}
                                fontSize={1}
                                fontWeight={500}
                                sx={{
                                    cursor: 'pointer',
                                    transition: 'all 0.2s ease',
                                    flex: ['1', '0 0 auto'],
                                    minHeight: '44px',
                                    '&:hover': {
                                        bg: 'accent.muted',
                                        borderColor: 'accent.emphasis'
                                    }
                                }}
                            >
                                Done
                            </Box>
                        </div>
                    </div>
                </div>
            )}
        </Box>
    );
};

export default DSAFlashcard; 